/**
 * What a session's pairings have done, folded from its own log (RFC 0301 §3f).
 *
 * Three entries make a firing, and none of them is a firing record: the
 * `mod.fired` note that a signal came through a pairing, the run queued on that
 * signal, and a `mod.passed` naming the run if the turn chose to do nothing.
 * Folding them rather than keeping a mutable `passed` flag means a turn that
 * passes writes one append and nothing has to be kept in step.
 */

import { topoOrder } from './projection-order'
import type {
  EntryId,
  FiringSignal,
  LogDoc,
  RunData,
  RunStatus,
  TimestampMs,
} from './types'
import { FIRED_EXTENSION, PASSED_EXTENSION } from './types'

const SIGNALS: ReadonlySet<string> = new Set(['edit', 'pause', 'comment'])

/** One occasion a pairing acted (§3f). */
export interface Firing {
  pairingId: string
  documentId: string
  signal: FiringSignal
  /** True when the turn called `pass`. What else it did is in the run. */
  passed: boolean
  /** The turn's run. Empty in the instant between the note and its run. */
  runId: EntryId
  /** Where that run is, so the gate can see a turn still queued. */
  runStatus: RunStatus | null
  /** The signal's own entry, which is the turn's prompt. */
  entryId: EntryId
  at: TimestampMs
}

/** Every firing on this log, oldest first. */
export function projectFirings(doc: LogDoc): Firing[] {
  const runsByPrompt = new Map<string, { id: EntryId; status: RunStatus }>()
  const passedRuns = new Set<string>()
  const fired: Array<Omit<Firing, 'passed' | 'runId' | 'runStatus'>> = []

  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type === 'run') {
      const run = p.payload as RunData
      const prompt = String(run.promptMessageId ?? '')
      // The first run on a prompt is the firing's turn. A later one on the same
      // entry is a retry of it, not a second firing.
      if (prompt && !runsByPrompt.has(prompt)) {
        runsByPrompt.set(prompt, { id: e.id, status: run.status })
      }
      continue
    }
    if (p.type !== 'extension') continue
    const raw = p.payload.payload
    if (p.payload.extensionType === PASSED_EXTENSION) {
      const runId = String(raw.runId ?? '')
      if (runId) passedRuns.add(runId)
    } else if (p.payload.extensionType === FIRED_EXTENSION) {
      const signal = String(raw.signal ?? '')
      const entryId = String(raw.entryId ?? '')
      if (!SIGNALS.has(signal) || !entryId) continue
      fired.push({
        pairingId: String(raw.pairingId ?? ''),
        documentId: String(raw.documentId ?? ''),
        signal: signal as FiringSignal,
        entryId,
        at: Number(raw.at ?? e.createdAt) || e.createdAt,
      })
    }
  }

  return fired.map((f) => {
    const run = runsByPrompt.get(f.entryId)
    return {
      ...f,
      runId: run?.id ?? '',
      runStatus: run?.status ?? null,
      passed: run ? passedRuns.has(run.id) : false,
    }
  })
}
